import {
  Avatar,
  Group as MantineGroup,
  Menu,
  SegmentedControl,
  Stack,
  Text,
} from '@mantine/core'
import type { HumanName } from '@medplum/fhirtypes'
import { HumanNameDisplay, ResourceAvatar } from '@medplum/react'
import { referenceDisplay } from '@seen/core/display'
import type { Group } from '@seen/core/groups'
import {
  IconExternalLink,
  IconLogout,
  IconSettings,
  IconSwitchHorizontal,
} from '@tabler/icons-react'
import type { JSX } from 'react'
import { useNavigate } from 'react-router'
import { DevTools } from '#src/components/app/header/DevTools.tsx'
import { useCurrentPractitioner } from '#src/hooks/useCurrentPractitioner.ts'
import { useCurrentPractitionerPerms } from '#src/hooks/useCurrentPractitionerPerms.ts'
import { useIsReadOnly } from '#src/hooks/useIsReadOnly.ts'
import { useMedplum } from '#src/hooks/useMedplum.ts'
import { usePortalStore } from '#src/hooks/usePortalStore.ts'
import { type Theme, useThemeStore } from '#src/hooks/useThemeStore.ts'

export interface HeaderDropdownProps {
  isStaging?: boolean
  /** App-supplied account actions (e.g. portal's MCP connection toggle). */
  accountMenuItems?: React.ReactNode
  children?: React.ReactNode
}

export function HeaderDropdown({
  isStaging,
  accountMenuItems,
  children,
}: HeaderDropdownProps): JSX.Element {
  const medplum = useMedplum()
  const practitioner = useCurrentPractitioner()
  const practitionerReferenceString = practitioner.reference.reference
  const logins = medplum.getLogins()
  const theme = useThemeStore((state) => state.theme)
  const setTheme = useThemeStore((state) => state.setTheme)
  const { groups } = useCurrentPractitionerPerms()
  const groupOverride = usePortalStore((state) => state.groupOverride)
  const setGroupOverride = usePortalStore((state) => state.setGroupOverride)
  const readOnly = useIsReadOnly()
  const navigate = useNavigate()
  return (
    <>
      <Stack align="center" p="xl">
        <ResourceAvatar
          size="xl"
          radius={100}
          value={practitioner.toFhirResource()}
        />
        <HumanNameDisplay value={practitioner?.name?.[0] as HumanName} />
        <Text c="dimmed" size="xs">
          {referenceDisplay(medplum.getActiveLogin()?.project)}
        </Text>
      </Stack>
      {logins.length > 1 && <Menu.Divider />}
      {logins.map(
        (login) =>
          login.profile.reference !== practitionerReferenceString && (
            <Menu.Item
              key={login.profile.reference}
              onClick={() => {
                void medplum
                  .setActiveLogin(login)
                  .then(() => window.location.reload())
              }}
            >
              <MantineGroup>
                <Avatar radius="xl" />
                <div style={{ flex: 1 }}>
                  <Text size="sm" fw={500}>
                    {referenceDisplay(login.profile)}
                  </Text>
                  <Text c="dimmed" size="xs">
                    {referenceDisplay(login.project)}
                  </Text>
                </div>
              </MantineGroup>
            </Menu.Item>
          )
      )}
      {isStaging && (
        <>
          <Menu.Divider />
          <Menu.Item
            leftSection={<IconExternalLink size={14} stroke={1.5} />}
            onClick={() => {
              window.open('https://seenehr.com', '_blank')
            }}
          >
            Open seenehr.com
          </Menu.Item>
        </>
      )}
      <Menu.Divider />
      <MantineGroup justify="center">
        <div className="text-sm font-medium">Access Group:</div>
        {groups.length > 1 && (
          <SegmentedControl
            size="xs"
            value={groupOverride}
            onChange={(newValue) => {
              if (!newValue) {
                setGroupOverride(undefined)
                return
              }

              setGroupOverride(newValue as Group)
            }}
            data={[
              { label: 'Default', value: '' },
              ...groups.map((group) => ({
                label: group,
                value: group,
              })),
            ]}
          />
        )}
        {groups.length > 1 && readOnly && (
          <div className="text-sm">(read-only)</div>
        )}
        {groups.length <= 1 && (
          <div className="text-sm">
            {groups.length === 1
              ? `${groups[0]}${readOnly ? ' (read-only)' : ''}`
              : 'No groups found'}
          </div>
        )}
      </MantineGroup>
      <Menu.Divider />
      <MantineGroup justify="center">
        <SegmentedControl
          size="xs"
          value={theme}
          onChange={(newValue) => setTheme(newValue as Theme)}
          data={[
            { label: 'Light', value: 'light' },
            { label: 'Dark', value: 'dark' },
            { label: 'Auto', value: 'system' },
          ]}
        />
      </MantineGroup>
      <Menu.Divider />
      <div className="px-3 py-2">
        <DevTools />
      </div>
      <Menu.Divider />
      <Menu.Item
        leftSection={<IconSwitchHorizontal size={14} stroke={1.5} />}
        onClick={() => navigate('/signin')}
      >
        Add another account
      </Menu.Item>
      <Menu.Item
        leftSection={<IconSettings size={14} stroke={1.5} />}
        onClick={() => navigate(`/practitioners/${practitioner.id}`)}
      >
        Account settings
      </Menu.Item>
      {accountMenuItems}
      <Menu.Item
        leftSection={<IconLogout size={14} stroke={1.5} />}
        onClick={async () => {
          await medplum.signOut()
          void navigate('/signin')
        }}
      >
        Sign out
      </Menu.Item>
      {children}
    </>
  )
}
